import React from "react";
import { StyleSheet, Text, View, Image, type ImageSourcePropType } from "react-native";

import { normalizeTextStyle } from "../Text/normalizeTextStyle";
import { useTheme, type Theme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { normalizeAvatarSize } from "./normalizeAvatarSize";
import { PersonIcon } from "./PersonIcon";

export type AvatarSize = "sm" | "md" | "lg";
export type AvatarVariant = "light" | "bold";
export type AvatarColor =
  | "neutral"
  | "earth"
  | "orange"
  | "magenta"
  | "purple"
  | "blue"
  | "aqua"
  | "green";

export interface AvatarProps {
  /**
   * Text to display in the avatar. If not provided, the avatar will display a person icon.
   */
  text?: string;

  /**
   * Image source to display in the avatar. Can be a URI string or require() image source.
   */
  image?: ImageSourcePropType;

  /**
   * Accessible alternative text.
   * Used if image is provided or if neither "image" nor "text" provided
   * @default none
   */
  accessibleLabel?: string;

  /**
   * Color of the avatar
   */
  color: AvatarColor;

  /**
   * Size of the avatar
   * @default 'md'
   */
  size?: AvatarSize;

  /**
   * Variant of the avatar
   * @default 'bold'
   */
  variant?: AvatarVariant;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

// Size mapping in pixels
const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 21,
  md: 40,
  lg: 110,
};

// Text level to font size
const FONT_SIZE_MAP: Record<AvatarSize, number> = {
  // getFontSizeStyle() because no font level maps to size 9.
  sm: 9,
  md: 14,
  lg: 36,
};

const ICON_SIZE_MAP: Record<AvatarSize, { width: number; height: number | undefined }> = {
  sm: { width: 9.3, height: 9.6 },
  md: { width: 17.5, height: 18 },
  lg: { width: 48, height: 50 },
};

const getBackgroundColor = (theme: Theme): Record<AvatarColor, Record<AvatarVariant, string>> => {
  return {
    neutral: {
      light: theme.colors.expressiveGrayWeak,
      bold: theme.colors.expressiveGrayStrong,
    },
    earth: {
      light: theme.colors.expressiveEarthWeak,
      bold: theme.colors.expressiveEarthStrong,
    },
    orange: {
      light: theme.colors.expressiveOrangeWeak,
      bold: theme.colors.expressiveOrangeStrong,
    },
    magenta: {
      light: theme.colors.expressiveMagentaWeak,
      bold: theme.colors.expressiveMagentaStrong,
    },
    purple: {
      light: theme.colors.expressivePurpleWeak,
      bold: theme.colors.expressivePurpleStrong,
    },
    blue: {
      light: theme.colors.expressiveBlueWeak,
      bold: theme.colors.expressiveBlueStrong,
    },
    aqua: {
      light: theme.colors.expressiveAquaWeak,
      bold: theme.colors.expressiveAquaStrong,
    },
    green: {
      light: theme.colors.expressiveGreenWeak,
      bold: theme.colors.expressiveGreenStrong,
    },
  };
};

const getTextColor = (theme: Theme): Record<AvatarColor, Record<AvatarVariant, string>> => {
  return {
    neutral: {
      light: theme.colors.expressiveGrayStrong,
      bold: theme.colors.foregroundInverse,
    },
    earth: {
      light: theme.colors.expressiveEarthStrong,
      bold: theme.colors.foregroundInverse,
    },
    orange: {
      light: theme.colors.expressiveOrangeStrong,
      bold: theme.colors.foregroundInverse,
    },
    magenta: {
      light: theme.colors.expressiveMagentaStrong,
      bold: theme.colors.foregroundInverse,
    },
    purple: {
      light: theme.colors.expressivePurpleStrong,
      bold: theme.colors.foregroundInverse,
    },
    blue: {
      light: theme.colors.expressiveBlueStrong,
      bold: theme.colors.foregroundInverse,
    },
    aqua: {
      light: theme.colors.expressiveAquaStrong,
      bold: theme.colors.foregroundInverse,
    },
    green: {
      light: theme.colors.expressiveGreenStrong,
      bold: theme.colors.foregroundInverse,
    },
  };
};

export const Avatar = ({
  text,
  image,
  accessibleLabel,
  color,
  size = "md",
  variant = "bold",
  sx,
}: AvatarProps): React.JSX.Element => {
  const theme = useTheme();

  const bgColor = React.useMemo(
    () => getBackgroundColor(theme)[color][variant],
    [color, theme, variant],
  );
  const textColor = React.useMemo(
    () => getTextColor(theme)[color][variant],
    [color, theme, variant],
  );
  const fontFamily = React.useMemo(() => theme.fonts.default, [theme]);
  const avatarSize = normalizeAvatarSize(SIZE_MAP[size]);
  const fontSize = FONT_SIZE_MAP[size];
  const useIcon = !image && (text === undefined || text === "");
  const iconSize = {
    width: normalizeAvatarSize(ICON_SIZE_MAP[size].width),
    height:
      ICON_SIZE_MAP[size].height === undefined
        ? undefined
        : normalizeAvatarSize(ICON_SIZE_MAP[size].height),
  };

  return (
    <View
      testID="avatar"
      accessibilityRole="image"
      accessible={!accessibleLabel && !text ? false : true}
      accessibilityLabel={accessibleLabel ?? text}
      style={[
        styles.container,
        {
          backgroundColor: bgColor,
          width: avatarSize,
          height: avatarSize,
          borderRadius: theme.borderRadius.full,
        },
        sx,
      ]}
    >
      {image ? (
        <Image
          testID="avatar-image"
          source={image}
          accessible={false}
          style={[
            styles.image,
            {
              width: avatarSize,
              height: avatarSize,
              borderRadius: theme.borderRadius.full,
            },
          ]}
        />
      ) : useIcon ? (
        <PersonIcon
          testID="person-icon"
          width={iconSize.width}
          height={iconSize.height}
          color={textColor}
          accessible={false}
        />
      ) : (
        // Note: this uses Text from React Native, not our Text component. This is because we don't want the default line heights applied here, we want the text to be centered vertically.
        <Text
          style={normalizeTextStyle(
            StyleSheet.flatten([
              styles.text,
              { fontFamily: fontFamily, color: textColor, fontSize: fontSize },
            ]),
          )}
        >
          {text}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
  text: {
    alignItems: "center",
    fontWeight: "700",
  },
  image: {
    resizeMode: "cover",
  },
});
